import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Shield, FileText } from 'lucide-react';
import { CANCELLATION_FEES, type TierType } from '@/components/portal/TierSelection';
import { FREE_TRIAL_DAYS, FREE_TRIAL_DOMAIN_LIMIT } from '@/lib/trial';
import { PUBLIC_TIER_LABELS } from '../../../shared/subscriptionPlans';
import {
  SERVICE_AGREEMENT_LAST_UPDATED_LABEL,
  SERVICE_AGREEMENT_TITLE,
} from '@/lib/serviceAgreement';

interface ServiceAgreementDialogProps {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
  clientName: string;
  clientEmail: string;
  selectedTier?: TierType;
}

export function ServiceAgreementDialog({
  open,
  onAccept,
  onDecline,
  clientName,
  clientEmail,
  selectedTier,
}: ServiceAgreementDialogProps) {
  const [agreed, setAgreed] = useState(false);
  const currentDate = new Date().toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const planName = selectedTier ? PUBLIC_TIER_LABELS[selectedTier] : null;
  const cancellationFee = selectedTier ? CANCELLATION_FEES[selectedTier] : null;
  const cancellationFeeText = cancellationFee
    ? `AUD $${cancellationFee}`
    : 'Essential AUD $50 or Premium AUD $99';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onDecline()}>
      <DialogContent className="max-w-2xl max-h-[90vh] cyber-card">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-6 w-6 text-primary" />
            <FileText className="h-5 w-5 text-muted-foreground" />
          </div>
          <DialogTitle className="text-xl font-mono">
            Service Agreement
          </DialogTitle>
          <DialogDescription>
            Please review and accept the ShadowScan Security Services Agreement
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-6 text-sm text-muted-foreground">
            {/* Client Information */}
            <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
              <h3 className="font-semibold text-foreground mb-2">Client Information</h3>
              <p><span className="text-muted-foreground">Client:</span> <span className="text-foreground">{clientName}</span></p>
              <p><span className="text-muted-foreground">Contact Email:</span> <span className="text-foreground">{clientEmail}</span></p>
              <p><span className="text-muted-foreground">Agreement Date:</span> <span className="text-foreground">{currentDate}</span></p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground">{SERVICE_AGREEMENT_TITLE}</h3>
              <p className="text-xs text-muted-foreground">Last updated: {SERVICE_AGREEMENT_LAST_UPDATED_LABEL}</p>
            </div>

            <div>
              <p>
                This Agreement is between <span className="text-primary font-semibold">Valnex Holdings trading as ShadowScan</span> (ShadowScan, Service Provider, we, us) and
                the Client (you) for the provision of managed WordPress security services for the website domain(s) added to the ShadowScan Portal (Sites).
              </p>
              <p className="mt-2">
                If you use the ShadowScan WordPress plugin as part of those Services, the <a className="text-primary underline-offset-4 hover:underline" href="https://shadowscan.com.au/plugin-addendum" target="_blank" rel="noopener noreferrer">ShadowScan Plugin Addendum</a> is incorporated into this Agreement and controls for plugin-specific behaviour where it is more specific.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">1. Authorisation & Access</h3>
              <p className="mb-2">You authorise ShadowScan to access the minimum systems reasonably required to deliver the Services for your Sites, which may include:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>WordPress administrator access (where provided by you)</li>
                <li>ShadowScan Plugin connectivity and telemetry</li>
                <li>Hosting control panel access (where provided by you)</li>
                <li>Other access you explicitly approve in writing (for example, DNS or edge/CDN configuration)</li>
              </ul>
              <p className="mt-2">
                ShadowScan will use approved connector access, tokens, and system information solely to deliver the Services and will not knowingly misuse, share, or exploit that access.
              </p>
              <p className="mt-2">
                You are responsible for maintaining valid access and ensuring the Sites remain reachable. If access is unavailable or restricted, some Services may be limited.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">2. Scope of Services</h3>
              <p className="mb-2">
                ShadowScan provides managed WordPress security designed to reduce security risk, detect suspicious activity early, and respond appropriately.
                No security service can eliminate all risk.
              </p>
              <p className="mb-2">Services may include, depending on plan and technical compatibility:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Continuous monitoring for security signals and suspicious behaviour</li>
                <li>Application-level protections and safe containment controls (where appropriate)</li>
                <li>Alert validation by security staff for high-risk events</li>
                <li>Security reporting through the Portal</li>
                <li>WordPress security hardening recommendations and configuration support</li>
                <li>Vulnerability awareness and prioritisation (core, plugins, themes)</li>
                <li>Login abuse detection and containment measures</li>
                <li>Guidance for remediation actions</li>
              </ul>
              <p className="mt-3 font-semibold text-foreground">Updates and patching</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>ShadowScan may automate or assist with updates where compatible and safe to do so.</li>
                <li>Updates may be paused or require approval where risk of disruption is identified.</li>
                <li>You remain responsible for maintaining backups and verifying business-critical functionality after updates unless a separate written arrangement applies.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">3. Plan Responsibilities</h3>
              <p className="font-semibold text-foreground">Essential - Guided Response</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>ShadowScan provides automatic protection controls, monitoring, and validated alerts.</li>
                <li>Where remediation is required, ShadowScan will provide clear guidance and recommended actions.</li>
                <li>You are responsible for executing remediation actions unless otherwise agreed in writing.</li>
              </ul>
              <p className="mt-3 font-semibold text-foreground">Premium - Managed Response</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Premium includes everything in Essential.</li>
                <li>ShadowScan may perform investigation, containment, and remediation actions on your behalf within reasonable scope, using access you provide.</li>
                <li>ShadowScan may apply emergency stabilisation actions where necessary to reduce immediate risk, prioritising safety and minimising disruption.</li>
                <li>You remain responsible for business decisions and approvals where required (for example, major configuration or functionality changes).</li>
              </ul>
              <p className="mt-3">
                Basic Hosting is a separate hosted onboarding offer and does not, by itself, include monitored security or analyst-led response.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">4. Fees, Billing, and Subscription</h3>
              <p className="font-semibold text-foreground">Published self-serve pricing (per domain per month)</p>
              <div className="p-4 bg-primary/10 rounded-lg border border-primary/30 space-y-2">
                <p>
                  <span className="text-primary font-bold">Essential AUD $50</span> per domain/month &nbsp;|&nbsp;
                  <span className="text-primary font-bold">Premium AUD $185</span> per domain/month
                </p>
                <p>
                  <span className="font-semibold text-foreground">Self-serve cancellation/removal fee schedule:</span>{' '}
                  <span className="text-accent font-bold">{cancellationFeeText}</span>{' '}
                  per domain{planName ? ` (${planName} plan)` : ''}
                </p>
              </div>
              <p className="mt-3 font-semibold text-foreground">Billing</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Services are billed per domain on a month-to-month subscription basis.</li>
                <li>Billing occurs on the 1st of each month for all active domains.</li>
                <li>Added domains are included under this Agreement and billed accordingly.</li>
                <li>Basic Hosting is a separate hosted onboarding offer and is not a managed security tier under this Agreement unless expressly stated otherwise in writing.</li>
                <li>Approved reviewed pricing paths do not use the published self-serve fee table.</li>
                <li>The exact custom rate, quantity, trial or billing-start terms, and any reviewed-pricing reassessment threshold are shown and accepted separately in the portal reviewed pricing schedule.</li>
                <li>The reviewed pricing schedule forms part of the accepted billing terms for that reviewed pricing activation.</li>
                <li>Eligible self-serve plans may include a {FREE_TRIAL_DAYS}-day free trial for up to {FREE_TRIAL_DOMAIN_LIMIT} domains, with billing starting only after the trial ends.</li>
                <li>Approved reviewed pricing paths may also begin with the same {FREE_TRIAL_DAYS}-day trial when offered in the portal, with billing starting after the trial ends.</li>
                <li>For self-serve plans, cancellation or removal of a domain incurs a fee of {cancellationFeeText} per domain unless the domain is removed before the end of an applicable {FREE_TRIAL_DAYS}-day free trial.</li>
                <li>Unless otherwise stated in writing or required by law, fees already charged are non-refundable and partial-month refunds do not apply for cancelled or removed domains.</li>
              </ul>
              <p className="mt-3 font-semibold text-foreground">Payment</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>A payment method is required before the self-serve free trial starts.</li>
                <li>Payment is required to maintain active service after the free trial or for non-trial activation paths.</li>
                <li>If payment is not received, ShadowScan may suspend or limit Services until billing resumes.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">5. Cancellation & Termination</h3>
              <p>
                You may cancel services or remove individual domains at any time. For self-serve plans, cancellation or removal of a domain incurs a
                <span className="text-accent font-semibold"> {cancellationFeeText} cancellation fee per domain{planName ? ` on the ${planName} plan` : ''}</span>.
                ShadowScan may suspend or terminate Services for non-payment or material breach of this Agreement.
              </p>
              <p className="mt-2">
                Reviewed pricing paths follow the cancellation, refund, and reassessment terms shown in the accepted reviewed pricing schedule for that activation.
              </p>
              <p className="mt-2">
                No cancellation fee applies if an eligible domain is removed before the end of the self-serve {FREE_TRIAL_DAYS}-day free trial.
              </p>
              <p className="mt-2">
                No partial-month refunds apply for cancelled or removed domains unless required by law or expressly stated otherwise in writing.
              </p>
              <p className="mt-2">
                Where reviewed pricing has been approved based on agreed portfolio size, domain count, spend level, or similar commercial assumptions, ShadowScan may review that pricing if those assumptions materially change. Any resulting pricing change applies prospectively from a future billing cycle unless otherwise agreed in writing.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">6. Client Responsibilities</h3>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Maintain current backups for your Sites</li>
                <li>Maintain lawful authority to request security changes for the Sites</li>
                <li>Keep WordPress, plugins, and themes reasonably supported (or accept risk if unsupported components remain)</li>
                <li>Provide timely access and approvals required to deliver Services</li>
                <li>Use the ShadowScan Portal and Plugin in accordance with reasonable security practices</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">7. Limitations and No Guarantees</h3>
              <p className="mb-2">
                ShadowScan uses reasonable efforts, tools, and processes intended to reduce security risk. However, cyber threats evolve and no service can ensure avoidance of all incidents.
              </p>
              <p>ShadowScan is not responsible for losses arising from:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Third-party hosting outages or misconfigurations outside ShadowScan control</li>
                <li>Compromised credentials not provided or managed by ShadowScan</li>
                <li>Unsupported or pirated software</li>
                <li>Actions taken by you or third parties that reduce security (including disabling protections)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">8. Limitation of Liability</h3>
              <p>
                To the extent permitted by law, ShadowScan’s liability is limited to the fees paid for the affected Site(s) in the preceding 30 days.
                ShadowScan is not liable for indirect or consequential loss (including loss of profit, revenue, or business interruption), except where liability cannot be excluded under applicable law.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">9. Confidentiality</h3>
              <p>
                Each party agrees to keep confidential any non-public information received from the other party in connection with the Services, except as required to deliver the Services or as required by law.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">10. Governing Law</h3>
              <p>
                This Agreement is governed by the laws of New South Wales, Australia. Each party submits to the non-exclusive jurisdiction of the courts of New South Wales and the Commonwealth courts competent to hear appeals from those courts.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-2">11. Entire Agreement</h3>
              <p>
                This Agreement, the accepted reviewed pricing schedule (if any), the ShadowScan Privacy Policy, and the ShadowScan Plugin Addendum (if you use the plugin) form the relevant customer-facing document set for the Services.
              </p>
              <p className="mt-2">
                If there is an inconsistency, the reviewed pricing schedule controls for pricing, the Plugin Addendum controls for plugin-specific behaviour, and this Agreement controls for managed service delivery unless the law requires otherwise.
              </p>
            </div>

            <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
              <h3 className="font-semibold text-foreground mb-2">Service Provider</h3>
              <p><span className="text-muted-foreground">Company:</span> <span className="text-foreground">Valnex Holdings trading as ShadowScan</span></p>
              <p><span className="text-muted-foreground">Website:</span> <span className="text-primary">shadowscan.com.au</span></p>
              <p><span className="text-muted-foreground">Contact:</span> <span className="text-foreground">support@shadowscan.com.au</span></p>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="flex-col gap-4 sm:flex-col">
          <div className="flex items-start gap-3 w-full p-3 bg-muted/30 rounded-lg border border-border/50">
            <Checkbox
              id="agree-terms"
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked === true)}
              className="mt-0.5"
            />
            <label
              htmlFor="agree-terms"
              className="text-sm text-muted-foreground cursor-pointer leading-relaxed"
            >
              I have read and agree to the ShadowScan Security Services Agreement. I understand that subscriptions are 
              <span className="text-foreground font-medium"> month-to-month with continuous billing</span>, and cancellation 
              incurs a <span className="text-accent font-medium">{cancellationFeeText} fee per domain{planName ? ` (${planName} plan)` : ''}</span>. If I use the ShadowScan WordPress plugin with the service, the ShadowScan Plugin Addendum also applies.
            </label>
          </div>

          <div className="flex gap-3 w-full">
            <Button
              variant="outline"
              onClick={onDecline}
              className="flex-1"
            >
              Decline
            </Button>
            <Button
              onClick={onAccept}
              disabled={!agreed}
              className="flex-1"
            >
              Accept & Create Account
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
